import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, Users, Search } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import api from '../lib/api';

export default function BookingWidget({ variant = 'hero' }) {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');
  const [reservationType, setReservationType] = useState('hospedaje');
  const [blockedDates, setBlockedDates] = useState([]);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  useEffect(() => {
    fetchBlockedDates();
  }, []);

  const fetchBlockedDates = async () => {
    try {
      const response = await api.get('/blocked-dates');
      const dates = [];
      
      // Add blocked dates
      response.data.blocked?.forEach(b => {
        dates.push(new Date(b.date));
      });
      
      // Add reservation dates
      response.data.reservations?.forEach(r => {
        const start = new Date(r.check_in);
        const end = r.check_out ? new Date(r.check_out) : start;
        let current = start;
        while (current < end) {
          dates.push(new Date(current));
          current = addDays(current, 1);
        }
      });
      
      setBlockedDates(dates);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (checkIn) params.set('checkIn', format(checkIn, 'yyyy-MM-dd'));
    if (checkOut) params.set('checkOut', format(checkOut, 'yyyy-MM-dd'));
    params.set('guests', guests);
    params.set('type', reservationType);
    navigate(`/reservar?${params.toString()}`);
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    
    return blockedDates.some(
      blocked => blocked.toDateString() === date.toDateString()
    );
  };

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;

  const isHero = variant === 'hero';

  return (
    <div
      data-testid="booking-widget"
      className={`${
        isHero
          ? 'bg-white/95 backdrop-blur-md border border-[#C8962D]/20 rounded-2xl p-6 shadow-[0_8px_30px_rgba(92,64,51,0.15)]'
          : 'bg-white rounded-xl p-4 border border-[#E0D8CC]'
      }`}
    >
      <div className={`grid gap-4 ${isHero ? 'md:grid-cols-5' : 'grid-cols-1'}`}>
        {/* Reservation Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#5C4033]">Tipo</label>
          <Select value={reservationType} onValueChange={setReservationType}>
            <SelectTrigger data-testid="reservation-type-select" className="border-[#E0D8CC]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hospedaje">Hospedaje</SelectItem>
              <SelectItem value="dia">Renta por Día</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Check-in */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#5C4033]">
            {reservationType === 'dia' ? 'Fecha' : 'Llegada'}
          </label>
          <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="checkin-date-btn"
                className="w-full justify-start text-left font-normal border-[#E0D8CC]"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-[#C8962D]" />
                {checkIn ? format(checkIn, 'dd MMM yyyy', { locale: es }) : 'Seleccionar'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkIn}
                onSelect={(date) => {
                  setCheckIn(date);
                  setCheckInOpen(false);
                  if (reservationType === 'dia') {
                    setCheckOut(null);
                  } else if (date) {
                    setCheckOut(null);
                    setTimeout(() => setCheckOutOpen(true), 150);
                  }
                }}
                disabled={isDateDisabled}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Check-out (only for hospedaje) */}
        {reservationType === 'hospedaje' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#5C4033]">Salida</label>
            <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="checkout-date-btn"
                  className="w-full justify-start text-left font-normal border-[#E0D8CC]"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-[#C8962D]" />
                  {checkOut ? format(checkOut, 'dd MMM yyyy', { locale: es }) : 'Seleccionar'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkOut}
                  onSelect={(date) => {
                    setCheckOut(date);
                    setCheckOutOpen(false);
                  }}
                  disabled={(date) => {
                    if (isDateDisabled(date)) return true;
                    if (checkIn && date <= checkIn) return true;
                    return false;
                  }}
                  defaultMonth={checkIn || undefined}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Guests */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#5C4033]">Huéspedes</label>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger data-testid="guests-select" className="border-[#E0D8CC]">
              <div className="flex items-center">
                <Users className="mr-2 h-4 w-4 text-[#C8962D]" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {[...Array(10)].map((_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>
                  {i + 1} {i === 0 ? 'huésped' : 'huéspedes'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search Button */}
        <div className={`${isHero ? 'flex items-end' : ''}`}>
          <Button
            onClick={handleSearch}
            data-testid="search-availability-btn"
            className="w-full bg-[#5C4033] hover:bg-[#3D5A3E] text-white h-10"
            disabled={!checkIn || (reservationType === 'hospedaje' && !checkOut)}
          >
            <Search className="mr-2 h-4 w-4" />
            {isHero ? 'Buscar' : 'Buscar Disponibilidad'}
          </Button>
        </div>
      </div>

      {/* Nights indicator */}
      {nights > 0 && (
        <p className="text-sm text-[#5C4033] mt-3 text-center">
          {nights} {nights === 1 ? 'noche' : 'noches'} seleccionadas
        </p>
      )}
    </div>
  );
}
